// src/content/figures/AiActRepresentativeDecision.jsx
// Inline SVG infographic: does a non-EU AI company need an AI Act authorised representative?
// Real text (indexable, selectable, screen-reader accessible). Explicit colours, no theme tokens.
//
// Grid (viewBox 760 wide):
//   left exit column   x=10–190  (c=100)
//   centre column      x=220–540 (c=380); Q3 widens to x=200–560
//   right exit column  x=570–750 (c=660)
//   Q4 pair            left x=20–360 (c=190) · right x=400–740 (c=570)
//   result row (4)     x=15–175 · 205–365 · 395–555 · 585–745 (each 160 wide, 30 gap)

const C = {
  ink: '#1e293b',
  muted: '#5b6472',
  line: '#94a3b8',
  boxFill: '#f3f6fb',
  boxStroke: '#cbd5e1',
  white: '#ffffff',
  accent: '#186be6',
};

const q = { fill: C.boxFill, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const end = { fill: C.white, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const win = { fill: C.accent, stroke: 'none', rx: 10 };
const t = { fontFamily: 'inherit', fontSize: 15, fill: C.ink };
const s = { fontFamily: 'inherit', fontSize: 13, fill: C.muted };
const lab = { fontFamily: 'inherit', fontSize: 13, fill: C.ink, fontWeight: 600 };
const onAccent = { fontFamily: 'inherit', fontSize: 15, fill: C.white, fontWeight: 600 };
const onAccentS = { fontFamily: 'inherit', fontSize: 13, fill: C.white };
const warn = { fontFamily: 'inherit', fontSize: 12.5, fill: '#7a4a00' };
const edge = { stroke: C.line, strokeWidth: 1.5, fill: 'none', markerEnd: 'url(#a22-arrow)' };

// Centred text lines inside a box: [text, style, dy-from-top]
function Lines({ cx, y, rows }) {
  return rows.map(([text, style, dy]) => (
    <text key={text} x={cx} y={y + dy} textAnchor="middle" {...style}>{text}</text>
  ));
}

function Q4({ x, y }) {
  const cx = x + 170;
  return (
    <g>
      <rect x={x} y={y} width="340" height="106" {...q} />
      <Lines cx={cx} y={y} rows={[
        ['Do you provide a general-purpose', t, 26],
        ['AI model?', t, 47],
        ['Qualifying open-source models without', s, 68],
        ['systemic risk are exempt from Article 54.', s, 86],
      ]} />
    </g>
  );
}

export default function AiActRepresentativeDecision() {
  return (
    <svg
      viewBox="0 0 760 1000"
      width="100%"
      role="img"
      aria-labelledby="a22-title a22-desc"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', maxWidth: 760 }}
    >
      <title id="a22-title">Do you need an AI Act authorised representative?</title>
      <desc id="a22-desc">
        Decision flow in four questions. One: is your company established in the EU? If yes, no AI Act representative is needed, because Articles 22 and 54 cover providers outside the EU. If no, question two: are you the provider, meaning you develop the AI system or model, or have it developed, and offer it under your own name, paid or free? If you only use or resell AI, no AI Act representative is needed; check GDPR Article 27 instead. Rebranding a high-risk system or repurposing a system as high-risk makes you its provider. If you are the provider, question three: is any AI system you provide high-risk, meaning an Annex III use such as hiring, credit scoring, education or biometrics, or a product or safety component under Annex I? Narrow exceptions apply to Annex III systems that pose no significant risk. Question four, on both branches: do you provide a general-purpose AI model? Open-source models that meet the licence and publication conditions, and have no systemic risk, are exempt from Article 54. High-risk and a model: appoint under both Article 22 and Article 54. High-risk only: appoint under Article 22, from 2 December 2027 for Annex III or 2 August 2028 for Annex I. Model only: appoint under Article 54, which has applied since 2 August 2025. Neither: no AI Act representative; check GDPR Article 27 instead.
      </desc>
      <defs>
        <marker id="a22-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.line} />
        </marker>
      </defs>

      {/* Q1 */}
      <rect x="220" y="20" width="320" height="80" {...q} />
      <Lines cx={380} y={20} rows={[
        ['Is your company established', t, 26],
        ['in the EU?', t, 47],
        ['(office, branch or subsidiary)', s, 67],
      ]} />

      {/* Q1 -> Yes (left) */}
      <path d="M 220 60 L 100 60 L 100 130" {...edge} />
      <text x="112" y="104" {...lab}>Yes</text>
      <rect x="10" y="130" width="180" height="120" {...end} />
      <Lines cx={100} y={130} rows={[
        ['No AI Act', t, 26],
        ['representative.', t, 47],
        ['Articles 22 and 54', s, 68],
        ['cover providers', s, 86],
        ['outside the EU.', s, 104],
      ]} />

      {/* Q1 -> No (down) */}
      <path d="M 380 100 L 380 150" {...edge} />
      <text x="392" y="130" {...lab}>No</text>

      {/* Q2 */}
      <rect x="220" y="150" width="320" height="120" {...q} />
      <Lines cx={380} y={150} rows={[
        ['Are you the provider?', t, 26],
        ['You develop it, or have it developed,', s, 47],
        ['and offer it under your own name or brand,', s, 65],
        ['paid or free.', s, 83],
      ]} />

      {/* Q2 -> No (right) */}
      <path d="M 540 210 L 660 210 L 660 240" {...edge} />
      <text x="552" y="176" {...lab}>No, we only</text>
      <text x="552" y="192" {...lab}>use or resell AI</text>
      <rect x="570" y="240" width="180" height="200" {...end} />
      <Lines cx={660} y={240} rows={[
        ['No AI Act', t, 26],
        ['representative.', t, 47],
        ['Check GDPR Article 27', s, 68],
        ['instead.', s, 86],
      ]} />
      <rect x="580" y="338" width="160" height="94" fill="#fff4e0" stroke="none" rx="6" />
      <Lines cx={660} y={338} rows={[
        ['Rebranding or', warn, 20],
        ['repurposing a system', warn, 36],
        ['as high-risk makes', warn, 52],
        ['you its provider.', warn, 68],
      ]} />

      {/* Q2 -> Yes (down) */}
      <path d="M 380 270 L 380 470" {...edge} />
      <text x="392" y="375" {...lab}>Yes</text>

      {/* Q3 */}
      <rect x="200" y="470" width="360" height="140" {...q} />
      <Lines cx={380} y={470} rows={[
        ['Is any AI system you provide high-risk?', t, 26],
        ['Annex III use (hiring, credit', s, 48],
        ['scoring, education, biometrics...)', s, 66],
        ['or an Annex I product or safety', s, 84],
        ['component.', s, 102],
        ['Narrow exceptions: Art. 6(3).', { ...s, fontStyle: 'italic' }, 124],
      ]} />

      {/* Q3 -> Yes / No into the two Q4 boxes */}
      <path d="M 260 610 L 260 650" {...edge} />
      <text x="272" y="636" {...lab}>Yes</text>
      <path d="M 500 610 L 500 650" {...edge} />
      <text x="512" y="636" {...lab}>No</text>

      {/* Q4 (left under Yes, right under No) */}
      <Q4 x={20} y={650} />
      <Q4 x={400} y={650} />

      {/* Q4 -> results */}
      <path d="M 95 756 L 95 806" {...edge} />
      <text x="107" y="786" {...lab}>Yes</text>
      <path d="M 285 756 L 285 806" {...edge} />
      <text x="297" y="786" {...lab}>No</text>
      <path d="M 475 756 L 475 806" {...edge} />
      <text x="487" y="786" {...lab}>Yes</text>
      <path d="M 665 756 L 665 806" {...edge} />
      <text x="677" y="786" {...lab}>No</text>

      {/* R1 */}
      <rect x="15" y="806" width="160" height="144" {...win} />
      <Lines cx={95} y={806} rows={[
        ['Appoint under', onAccent, 26],
        ['both Article 22', onAccent, 47],
        ['and Article 54.', onAccent, 68],
        ['Both duties apply.', onAccentS, 92],
      ]} />
      {/* R2 */}
      <rect x="205" y="806" width="160" height="144" {...win} />
      <Lines cx={285} y={806} rows={[
        ['Appoint under', onAccent, 26],
        ['Article 22.', onAccent, 47],
        ['From 2 Dec 2027', onAccentS, 71],
        ['(Annex III) or', onAccentS, 89],
        ['2 Aug 2028', onAccentS, 107],
        ['(Annex I).', onAccentS, 125],
      ]} />
      {/* R3 */}
      <rect x="395" y="806" width="160" height="144" {...win} />
      <Lines cx={475} y={806} rows={[
        ['Appoint under', onAccent, 26],
        ['Article 54.', onAccent, 47],
        ['Applies since', onAccentS, 71],
        ['2 Aug 2025.', onAccentS, 89],
      ]} />
      {/* R4 */}
      <rect x="585" y="806" width="160" height="144" {...end} />
      <Lines cx={665} y={806} rows={[
        ['No AI Act', t, 26],
        ['representative.', t, 47],
        ['Check GDPR', s, 71],
        ['Article 27 instead.', s, 89],
      ]} />

      {/* Footnote */}
      <text x="380" y="982" textAnchor="middle" {...s}>Dates per Regulation (EU) 2026/1744. Gabsor's summary, not legal advice.</text>
    </svg>
  );
}
